import React, { useState, useMemo, useContext, useCallback } from 'react';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { CheckCircle2, Pencil, Loader2, RotateCcw, Cpu } from 'lucide-react';
import FieldConfidenceBadge from './FieldConfidenceBadge';
import ConflictResolverCard from './ConflictResolverCard';
import AISuggestionPanel from './AISuggestionPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  address: 'Property Address',
  postcode: 'Postcode',
  valuation_date: 'Valuation Date',
  inspection_date: 'Inspection Date',
  property_type: 'Property Type',
  property_interest: 'Tenure',
  building_age: 'Building Age',
  gia: 'Gross Internal Area',
  area_units: 'Area Units',
  storeys: 'Storeys',
  roof_type: 'Roof Type',
  wall_type: 'Wall Type',
  windows: 'Windows',
  services: 'Services',
  epc_rating: 'EPC Rating',
  council_tax: 'Council Tax Band',
  notes: 'Notes',
};

export default function ExtractedFieldsReview({ fields, conflicts, onConfirm, onCancel, loading, jobId }) {
  const { token } = useContext(AuthContext);

  const fieldEntries = useMemo(() => {
    if (!fields || !fields.fields) return [];
    return Object.entries(fields.fields).map(([key, data]) => ({
      key,
      label: FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value: data.value,
      confidence: data.confidence || {},
      sourceFile: data.source_file || '',
      sourcePage: data.source_page || 1,
    }));
  }, [fields]);

  const [editedValues, setEditedValues] = useState(() => {
    const initial = {};
    fieldEntries.forEach(f => {
      initial[f.key] = typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value ?? '');
    });
    return initial;
  });

  const [editingField, setEditingField] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRejected, setAiRejected] = useState({});

  const handleEdit = (key, value) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const handleConflictResolve = (fieldKey, value) => {
    setEditedValues(prev => ({ ...prev, [fieldKey]: String(value ?? '') }));
  };

  const handleConfirm = () => {
    onConfirm(editedValues);
  };

  // AI suggestion handlers
  const handleRunAI = useCallback(async () => {
    if (!jobId || !token) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/inspection-import/${jobId}/ai-suggest`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.suggestions) {
        const map = {};
        data.suggestions.forEach(s => {
          if (s.suggestion) map[s.field_key] = s;
        });
        setAiSuggestions(map);
      }
    } catch (err) {
      console.error('AI suggestion failed:', err);
    } finally {
      setAiLoading(false);
    }
  }, [jobId, token]);

  const handleAcceptAI = (fieldKey, value) => {
    setEditedValues(prev => ({ ...prev, [fieldKey]: String(value ?? '') }));
    setAiSuggestions(prev => {
      const copy = { ...prev };
      delete copy[fieldKey];
      return copy;
    });
  };

  const handleRejectAI = (fieldKey) => {
    setAiRejected(prev => ({ ...prev, [fieldKey]: true }));
    setAiSuggestions(prev => {
      const copy = { ...prev };
      delete copy[fieldKey];
      return copy;
    });
  };

  const hasEligibleFields = fieldEntries.some(f => (f.confidence.score || 0) < 75);
  const summary = fields?.summary || {};

  return (
    <div className="space-y-4" data-testid="extracted-fields-review">
      {/* Summary bar */}
      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border text-sm">
        <span className="font-medium">{summary.total_fields || fieldEntries.length} fields extracted</span>
        <div className="flex gap-2">
          {summary.high_confidence > 0 && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              {summary.high_confidence} high
            </Badge>
          )}
          {summary.medium_confidence > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              {summary.medium_confidence} medium
            </Badge>
          )}
          {summary.low_confidence > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              {summary.low_confidence} low
            </Badge>
          )}
        </div>
        {hasEligibleFields && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRunAI}
            disabled={aiLoading}
            className="ml-auto text-xs"
            data-testid="run-ai-suggest-btn"
          >
            {aiLoading ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Cpu className="h-3 w-3 mr-1" />
            )}
            AI Suggest
          </Button>
        )}
      </div>

      {/* Conflicts section */}
      {conflicts && conflicts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-amber-700">Conflicts to Resolve ({conflicts.length})</h4>
          {conflicts.map((c, i) => (
            <ConflictResolverCard key={i} conflict={c} onResolve={handleConflictResolve} />
          ))}
          <Separator />
        </div>
      )}

      {/* Fields list */}
      <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
        {fieldEntries.map((field) => {
          const suggestion = aiSuggestions[field.key];
          const rejected = aiRejected[field.key];

          return (
            <div
              key={field.key}
              className="p-3 rounded-lg border bg-white hover:border-gray-300 transition-colors"
              data-testid={`field-row-${field.key}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-40 flex-shrink-0">
                  <p className="text-sm font-medium text-gray-700">{field.label}</p>
                  <p className="text-xs text-gray-400 truncate">{field.sourceFile}</p>
                </div>

                <div className="flex-1 min-w-0">
                  {editingField === field.key ? (
                    <Input
                      value={editedValues[field.key] || ''}
                      onChange={(e) => handleEdit(field.key, e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                      autoFocus
                      className="h-8 text-sm"
                      data-testid={`field-edit-input-${field.key}`}
                    />
                  ) : (
                    <p className="text-sm text-gray-900 truncate font-mono">
                      {editedValues[field.key] || '-'}
                    </p>
                  )}
                </div>

                <FieldConfidenceBadge
                  level={field.confidence.level}
                  score={field.confidence.score}
                />

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField(editingField === field.key ? null : field.key)}
                  className="flex-shrink-0"
                  data-testid={`edit-field-${field.key}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* AI Suggestion row */}
              {suggestion && !rejected && (
                <AISuggestionPanel
                  suggestion={suggestion}
                  onAccept={handleAcceptAI}
                  onReject={handleRejectAI}
                />
              )}
            </div>
          );
        })}

        {fieldEntries.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No fields were extracted. Try uploading a clearer document.</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="review-cancel-btn">
          <RotateCcw className="h-4 w-4 mr-2" />
          Start Over
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || fieldEntries.length === 0}
          data-testid="review-confirm-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4 mr-2" />
          )}
          Confirm & Populate ({Object.keys(editedValues).length} fields)
        </Button>
      </div>
    </div>
  );
}
